import { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, CircularProgress, Alert, Grid, Paper } from '@mui/material';

interface Item { token: string; name: string }
interface Feed {
  entityName: string;
  industry: string;
  nowServing: Item[];
  waiting: Item[];
  waitingCount: number;
}

/**
 * Lobby "now serving" board for a standalone-vertical entity (campus/centre/store).
 * Path: /entity/:vertical/:entityId. Polls /api/entity-display/:vertical/:entityId.
 */
const EntityDisplay = () => {
  const [data, setData] = useState<Feed | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  const timer = useRef<any>(null);

  // vertical + entityId from /entity/<vertical>/<entityId>
  const parts = window.location.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('entity');
  const vertical = idx >= 0 ? parts[idx + 1] : '';
  const entityId = idx >= 0 ? parts[idx + 2] : '';

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const apiUrl = (import.meta as any).env?.VITE_API_URL || 'https://api.qplease.in';
    const poll = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/entity-display/${vertical}/${entityId}`);
        if (!res.ok) throw new Error('Display not available');
        const json = await res.json();
        if (!cancelled) { setData(json.data || json); setError(''); }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load the display');
      } finally {
        if (!cancelled) timer.current = setTimeout(poll, 5000);
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [vertical, entityId]);

  if (error && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 20 }}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 20 }}><CircularProgress size={80} /></Box>;
  }

  return (
    <Container maxWidth={false} sx={{ py: 4, minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 4 }}>
        <Typography variant="h2" sx={{ fontWeight: 300 }}>{data.entityName}</Typography>
        <Typography variant="h4" sx={{ color: 'text.secondary' }}>{now.toLocaleTimeString()}</Typography>
      </Box>

      <Typography variant="h4" sx={{ color: '#4caf50', mb: 2, letterSpacing: 2 }}>NOW SERVING</Typography>
      {data.nowServing.length === 0 ? (
        <Typography variant="h3" sx={{ color: 'text.secondary', mb: 4 }}>—</Typography>
      ) : (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {data.nowServing.map((s, i) => (
            <Grid key={s.token + i} size={{ xs: 6, sm: 4, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#16331c', border: '2px solid #4caf50' }}>
                <Typography sx={{ fontSize: '3.5rem', fontWeight: 800, lineHeight: 1 }}>{s.token}</Typography>
                {s.name && <Typography variant="h6" sx={{ color: 'text.secondary', mt: 1 }}>{s.name}</Typography>}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Typography variant="h4" sx={{ color: '#2196f3', mb: 2, letterSpacing: 2 }}>
        WAITING · {data.waitingCount}
      </Typography>
      <Grid container spacing={2}>
        {data.waiting.map((w, i) => (
          <Grid key={w.token + i} size={{ xs: 4, sm: 3, md: 2 }}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#13233a' }}>
              <Typography sx={{ fontSize: '2rem', fontWeight: 600 }}>{w.token}</Typography>
            </Paper>
          </Grid>
        ))}
        {data.waiting.length === 0 && <Typography variant="h5" sx={{ color: 'text.secondary', ml: 2 }}>No one waiting</Typography>}
      </Grid>
    </Container>
  );
};

export default EntityDisplay;
